<script setup lang="ts">
import { computed, ref, watch, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useSystemStore } from '@/stores/system'
import { useWidgetStore } from '@/stores/widget'
import { getPreferredNavigatorLocale, setLocale } from '@/modules/i18n'
import BottomNavigation from '@/components/BottomNavigation.vue'
import SnackbarControl from '@/components/SnackbarControl.vue'
import DialogControl from '@/components/DialogControl.vue'
import InstallGuide from '@/components/InstallGuide.vue'

const { t, locale } = useI18n()
const route = useRoute()
const systemStore = useSystemStore()
const widgetStore = useWidgetStore()

const showBottomNav = ref(false)
const isUpdateAvailable = computed(() => systemStore.isUpdateAvailable)
const serviceWorkerRegistration = computed(
  () => systemStore.serviceWorkerRegistration
)

watch(route, (resolvedRoute) => {
  showBottomNav.value = !resolvedRoute.meta.hideNav
})

watch(isUpdateAvailable, (hasNewContent) => {
  if (hasNewContent) {
    // TODO: Prevent firing this on first load
    widgetStore.createSnackbar({
      text: t('updateAvailable'),
      actionText: t('refresh'),
      isInfinite: true,
      action: () => {
        systemStore.isUpdateAvailable = false
        serviceWorkerRegistration.value?.waiting?.postMessage({
          action: 'SKIP_WAITING'
        })
      }
    })
  }
})

onBeforeMount(() => {
  // We only care about the base locale
  const preferredBaseLocale = getPreferredNavigatorLocale().split('-').shift()
  if (preferredBaseLocale) {
    setLocale(preferredBaseLocale)
  }
})
</script>

<template>
  <div id="view">
    <RouterView v-slot="{ Component }">
      <Suspense>
        <div class="flex-1">
          <Component :is="Component" :key="[$route.fullPath, locale]" />
        </div>
      </Suspense>
    </RouterView>
    <BottomNavigation v-if="showBottomNav" />
    <InstallGuide />
    <SnackbarControl />
    <DialogControl />
  </div>
</template>

<i18n>
{
  "en": {
    "updateAvailable": "Update available.",
    "refresh": "Refresh"
  },
  "de": {
    "updateAvailable": "Update verfügbar.",
    "refresh": "Refreshen"
  }
}
</i18n>
